import { loadCytoscape } from '../../../inventoree/map/draw'
import { arrangeMap, arrangeStyle, layoutOptions, registerLayouts, type ArrangeCy } from './layouts'
import { groupKeyFromId, type MapView } from './grouping'
import { layoutOrphans, presetLayout, presetPlan, readPositions, type Positions } from './positions'

/**
 * Desenul hartii de dependente - `AdmMap` din `public/js/adm.js`.
 *
 * BIBLIOTECA E A PORTALULUI. Cytoscape sta deja in depozit, la
 * `public/plugins/cytoscape/cytoscape.min.js`, si se incarca de acolo ca orice
 * alt fisier al portalului - nu e o dependinta noua si nu are ce cauta in
 * `package.json`. Incarcatorul e cel scris pentru harta de inventar
 * (`inventoree/map/draw.ts`): un singur `<script>` pentru amandoua ecranele, si
 * al doilea il gaseste deja pus.
 *
 * CE SPUNE DESENUL, si prin ce canal - fiindca un desen care spune doua lucruri
 * prin acelasi canal nu spune niciunul:
 *
 *   FORMA nodului = ce fel de lucru e (aparat, aplicatie, serviciu IT,
 *     serviciu de afaceri);
 *   CULOAREA nodului = cat de important e. Ce n-are treapta pusa e neutru, nu
 *     verde: „nu stiu" nu e acelasi lucru cu „nu conteaza";
 *   HALOUL din jur = starea de ACUM, cazut sau degradat. In afara nodului, ca
 *     sa nu spele culoarea de importanta si sa nu se bata cu conturul, unde
 *     stau deja radacina si selectia. Un nod radacina, selectat si cazut le
 *     arata pe toate trei;
 *   GROSIMEA sagetii = de cate ori s-a vazut legatura, deci una intamplatoare
 *     iese fir de par langa una statornica;
 *   LINIA INTRERUPTA = legatura a incetat sa mai apara.
 *
 * PULSATIA E IN PLUS, NU IN LOC. Cine si-a cerut mai putina miscare din sistem
 * primeste aceeasi harta fara clipit: haloul ramane aprins pe valoarea lui
 * tare, deci starea se citeste in continuare. Daca repausul ar fi fost cel
 * stins, oprirea miscarii ar fi ascuns tocmai elementul cazut.
 */

/** Treptele de importanta - `CRITICALITY_COLOUR` din adm.js. */
const CRITICALITY_COLOUR: Record<number, string> = {
  5: '#c0392b',
  4: '#e67e22',
  3: '#f1c40f',
  2: '#95a5a6',
  1: '#aab4bf',
}

/** `Ci_classes::KIND_*` - aparat, aplicatie, serviciu IT, serviciu de afaceri. */
const KIND_SHAPE: Record<number, string> = {
  1: 'round-rectangle',
  2: 'ellipse',
  3: 'diamond',
  4: 'hexagon',
}

export const HEALTH_DOWN = 3
export const HEALTH_DEGRADED = 2

/* Aceleasi doua culori ca `.adm-pill-bad` / `.adm-pill-warn` si ca randurile
   colorate din liste, deci acelasi lucru arata la fel pe toate ecranele. */
const DOWN_COLOUR = '#d64635'
const DEGRADED_COLOUR = '#f0932b'

/*
 * Pulsatia CRESTE, nu doar se stinge: o pata care se ingalbeneste si revine
 * trece neobservata cu coada ochiului. Un inel care se largeste si se subtiaza
 * e miscare adevarata - se prinde din privirea periferica, care la asta e buna.
 *
 * Cel cazut bate mai des si merge mai departe; degradatul, rar si scurt.
 * Degradatele sunt multe - prin propagare, o alarma pe un switch le da starea
 * asta tuturor stramosilor - deci daca ar clipi la fel, harta ar deveni un
 * singur puls mare si nu s-ar mai vedea cine e cauza.
 */
const DOWN_PADDING = 16
const DOWN_OPACITY = 0.75
const DOWN_PADDING_MAX = 32
const DOWN_DIM = 0.06
const DEGRADED_PADDING = 9
const DEGRADED_OPACITY = 0.6
const DEGRADED_PADDING_MAX = 18
const DEGRADED_DIM = 0.18
const PULSE_MS_DOWN = 900
const PULSE_MS_DEGRADED = 1600

type CyElement = { data: (key?: string) => Record<string, unknown> | string; addClass: (c: string) => void; removeClass: (c: string) => void }

type CyCollection = {
  length: number
  forEach: (fn: (el: CyElement) => void) => void
  map: <T>(fn: (el: CyElement) => T) => T[]
  addClass: (c: string) => void
  removeClass: (c: string) => void
}

export type Cy = {
  destroy: () => void
  ready: (fn: () => void) => void
  nodes: (selector?: string) => CyCollection
  edges: (selector?: string) => CyCollection
  getElementById: (id: string) => CyElement & { length: number }
  batch: (fn: () => void) => void
  on: (events: string, selector: string | ((ev: unknown) => void), handler?: (ev: { target: CyElement }) => void) => void
  fit: (eles: unknown, padding: number) => void
  zoom: ((level?: number | { level: number; renderedPosition: { x: number; y: number } }) => number) & ((o: unknown) => void)
  pan: (position?: { x: number; y: number }) => { x: number; y: number }
  center: () => void
  width: () => number
  height: () => number
  resize: () => void
  png: (options: Record<string, unknown>) => string
  container: () => HTMLElement
}

export function reducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** Stilurile hartii - `style()` din adm.js, rand cu rand. */
export function mapStyle(): object[] {
  return [
    {
      selector: 'node',
      style: {
        label: 'data(label)',
        'font-size': '11px',
        'font-family': 'inherit',
        color: '#4a5560',
        'text-valign': 'bottom',
        'text-margin-y': 6,
        'text-background-color': '#f7f9fa',
        'text-background-opacity': 0.85,
        'text-background-padding': 2,
        width: 30,
        height: 30,
        'background-color': (node: CyElement) => CRITICALITY_COLOUR[Number(node.data('criticality'))] || '#bdc3c7',
        shape: (node: CyElement) => KIND_SHAPE[Number(node.data('kind'))] || 'round-rectangle',
        'border-width': 3,
        'border-color': '#ffffff',
        'overlay-opacity': 0,
      },
    },
    {
      selector: 'node.root',
      style: { 'border-color': '#3598dc', 'border-width': 4, width: 42, height: 42, 'font-weight': 'bold' },
    },
    { selector: 'node:selected', style: { 'border-color': '#3598dc', 'border-width': 4 } },
    /*
     * `underlay`, nu `overlay`: haloul se deseneaza SUB nod. Deasupra, la
     * opacitatea care se vede de la distanta, ar spala tocmai culoarea de
     * importanta - adica starea ar manca informatia de dedesubt.
     */
    {
      selector: 'node.health-degraded',
      style: {
        'underlay-color': DEGRADED_COLOUR,
        'underlay-padding': DEGRADED_PADDING,
        'underlay-opacity': DEGRADED_OPACITY,
        'transition-property': 'underlay-opacity underlay-padding',
        'transition-duration': '0.9s',
      },
    },
    {
      selector: 'node.health-down',
      style: {
        'underlay-color': DOWN_COLOUR,
        'underlay-padding': DOWN_PADDING,
        'underlay-opacity': DOWN_OPACITY,
        'transition-property': 'underlay-opacity underlay-padding',
        'transition-duration': '0.55s',
      },
    },
    {
      selector: 'node.health-down.pulse-on',
      style: { 'underlay-padding': DOWN_PADDING_MAX, 'underlay-opacity': DOWN_DIM },
    },
    {
      selector: 'node.health-degraded.pulse-on-soft',
      style: { 'underlay-padding': DEGRADED_PADDING_MAX, 'underlay-opacity': DEGRADED_DIM },
    },
    {
      selector: 'edge',
      style: {
        'curve-style': 'bezier',
        'target-arrow-shape': 'triangle',
        'arrow-scale': 0.8,
        'line-color': '#cfd6dc',
        'target-arrow-color': '#cfd6dc',
        'font-size': '10px',
        'font-family': 'inherit',
        label: 'data(label)',
        'text-wrap': 'wrap',
        'text-rotation': 'autorotate',
        'text-background-color': '#f7f9fa',
        'text-background-opacity': 0.9,
        'text-background-padding': 2,
        color: '#98a4b0',
        /* Grosimea poarta de cate ori s-a vazut legatura. */
        width: (edge: CyElement) => Math.min(6, 1 + Math.log10(Number(edge.data('observations')) || 1)),
      },
    },
    {
      selector: 'edge.gone',
      style: { 'line-style': 'dashed', 'line-color': '#e17055', 'target-arrow-color': '#e17055' },
    },
    ...groupStyle(),
  ]
}

/**
 * Casetele si ce iese din restrangerea lor - vezi `grouping.ts`.
 *
 * Caseta deschisa: contur punctat si numele sus, ca la harta de inventar
 * (`officeBoxes.ts`). Caseta restransa: un nod mai mare, cu contur dublu, ca
 * sa nu se confunde cu un aparat; culoarea si haloul vin din membrii ei.
 * Muchia adunata e mai groasa si poarta numarul.
 */
function groupStyle(): object[] {
  return [
    {
      selector: 'node.grp-box',
      style: {
        shape: 'round-rectangle',
        'background-color': '#eef3f7',
        'background-opacity': 0.55,
        'border-width': 1.5,
        'border-style': 'dashed',
        'border-color': '#8fa3b5',
        label: 'data(label)',
        'text-valign': 'top',
        'text-halign': 'center',
        'text-margin-y': -4,
        'font-size': '12px',
        'font-weight': 'bold',
        color: '#4a5560',
        padding: '18px',
      },
    },
    {
      selector: 'node.grp-collapsed',
      style: {
        shape: 'round-rectangle',
        width: 56,
        height: 40,
        'border-width': 4,
        'border-style': 'double',
        'border-color': '#5b6f82',
        'font-weight': 'bold',
        'text-wrap': 'wrap',
        'text-max-width': '160px',
      },
    },
    { selector: 'node.grp-multi', style: { 'text-wrap': 'wrap', 'text-max-width': '180px' } },
    {
      selector: 'edge.grp-agg',
      style: {
        'line-color': '#8fa3b5',
        'target-arrow-color': '#8fa3b5',
        color: '#5b6f82',
        'font-weight': 'bold',
        width: (edge: CyElement) => Math.min(8, 2 + Math.log2(Number(edge.data('count')) || 1)),
      },
    },
  ]
}

/**
 * Asezarea pentru cytoscape - logica sta in `layouts.ts`. Pe toate asezarile
 * in afara de forta, cytoscape primeste `preset`, iar pozitiile le pune
 * `arrangeMap()` imediat dupa desen, cu pasul care garanteaza ca nimic nu se
 * suprapune.
 */
export function layoutFor(view: string, count: number, layout: string = 'auto'): object {
  return layoutOptions(view, count, layout)
}

export interface DrawGraph {
  nodes: { id: number; health: number }[]
  edges: { id: number; source: number; target: number; status: number }[]
}

export interface DrawOptions {
  container: HTMLElement
  graph: DrawGraph
  root: number
  view: string
  /** `Arrange` din bara: auto, tiers, force, hierarchy, grid, concentric. Lipsa = auto. */
  layout?: string
  /** Zoomul si pozitia pe care le-a ales omul, cand le-a ales el. */
  viewport: { zoom: number; pan: { x: number; y: number } } | null
  onNode: (id: number) => void
  onViewportMoved: () => void
  onViewportChange: (viewport: { zoom: number; pan: { x: number; y: number } }) => void
  /** Pozitii salvate (un view, sau ultima stare a omului) - vezi `map/positions.ts`. */
  positions?: Positions | null
  /** Un nod tras cu mausul si lasat: pozitiile de acum ale tuturor. */
  onNodesMoved?: (positions: Positions) => void
  /** Casetele si filtrele (`grouping.ts`). Lipsa = graful ca atare. */
  shown?: MapView | null
  /** Clic pe o caseta: restrange-o sau deschide-o. */
  onGroup?: (key: string) => void
}

function healthClass(health: number | undefined): string {
  if (health === HEALTH_DOWN) return 'health-down'
  if (health === HEALTH_DEGRADED) return 'health-degraded'
  return ''
}

/** Elementele panzei din graful filtrat si grupat. Parintii intai. */
export function viewElements(view: MapView, root: number): object[] {
  return [
    ...view.boxes.map((box) =>
      box.collapsed
        ? {
            data: { id: box.id, groupKey: box.key, label: box.label, kind: 0, criticality: box.criticality, health: box.health, count: box.count },
            classes: ['grp-collapsed', healthClass(box.health)].filter(Boolean).join(' '),
          }
        : { data: { id: box.id, groupKey: box.key, label: box.label, count: box.count }, classes: 'grp-box' },
    ),
    ...view.nodes.map(({ node, parent, alsoIn }) => {
      const classes: string[] = []
      if (node.id === root) classes.push('root')
      const health = healthClass(node.health)
      if (health) classes.push(health)
      const data: Record<string, unknown> = { ...node, id: String(node.id) }
      if (parent) data.parent = parent
      if (alsoIn && alsoIn.length > 0) {
        data.label = `${node.label}
(also ${alsoIn.join(', ')})`
        classes.push('grp-multi')
      }
      return { data, classes: classes.join(' ') }
    }),
    ...view.edges.map((edge) => ({
      data: edge.edge
        ? { ...edge.edge, id: edge.id, source: edge.source, target: edge.target }
        : { id: edge.id, source: edge.source, target: edge.target, label: edge.label, count: edge.count, observations: edge.observations, status: edge.status, type: edge.type },
      classes: [edge.status === 0 ? 'gone' : '', edge.edge ? '' : 'grp-agg'].filter(Boolean).join(' '),
    })),
  ]
}

/**
 * Graful pe care se pun pozitiile salvate: nodurile care se deseneaza ca noduri.
 * Cu casete, membrii plus casetele restranse; o caseta deschisa e doar conturul
 * membrilor ei si n-are pozitie a ei.
 */
export function planGraph(options: Pick<DrawOptions, 'graph' | 'shown'>): {
  nodes: { id: string }[]
  edges: { source: string; target: string }[]
} {
  if (!options.shown) {
    return {
      nodes: options.graph.nodes.map((n) => ({ id: String(n.id) })),
      edges: options.graph.edges.map((e) => ({ source: String(e.source), target: String(e.target) })),
    }
  }
  return {
    nodes: [
      ...options.shown.boxes.filter((b) => b.collapsed).map((b) => ({ id: b.id })),
      ...options.shown.nodes.map(({ node }) => ({ id: String(node.id) })),
    ],
    edges: options.shown.edges.map((e) => ({ source: String(e.source), target: String(e.target) })),
  }
}

/** Cronometrele pulsatiei, ca sa poata fi oprite inainte de `destroy`. */
export function startPulse(cy: Cy): number[] {
  if (reducedMotion()) return []

  const timers: number[] = []

  const beat = (selector: string, className: string, every: number) => {
    const pulse = () => {
      const group = cy.nodes(selector)
      if (group.length === 0) return
      group.addClass(className)
      window.setTimeout(() => group.removeClass(className), Math.round(every / 2))
    }
    pulse()
    timers.push(window.setInterval(pulse, every))
  }

  beat('.health-down', 'pulse-on', PULSE_MS_DOWN)
  beat('.health-degraded', 'pulse-on-soft', PULSE_MS_DEGRADED)

  return timers
}

/**
 * Poza intregului graf, nu a ferestrei.
 *
 * `full: true`, si nu din belsug: o poza dintr-un colt al hartii nu probeaza
 * nimic la un audit. `scale: 2` ca etichetele sa ramana citete cand foaia se
 * tipareste, si fundal alb fiindca pe hartie transparentul iese negru.
 */
export function mapPng(cy: Cy): string {
  return cy.png({ full: true, scale: 2, bg: '#ffffff' })
}

export async function drawMap(options: DrawOptions): Promise<Cy> {
  const cytoscape = (await loadCytoscape()) as unknown as (opts: Record<string, unknown>) => Cy

  const elements = options.shown ? viewElements(options.shown, options.root) : [
    ...options.graph.nodes.map((node) => {
      const classes: string[] = []
      if (node.id === options.root) classes.push('root')
      /* Lipsa cheii - server vechi, inainte de migrare - se citeste ca
         operational, deci nodul ramane exact cum arata pana acum. */
      if (node.health === HEALTH_DOWN) classes.push('health-down')
      else if (node.health === HEALTH_DEGRADED) classes.push('health-degraded')
      return { data: { ...node, id: String(node.id) }, classes: classes.join(' ') }
    }),
    ...options.graph.edges.map((edge) => ({
      data: { ...edge, id: 'e' + edge.id, source: String(edge.source), target: String(edge.target) },
      classes: edge.status === 0 ? 'gone' : '',
    })),
  ]

  registerLayouts(cytoscape)

  /* Cu pozitii salvate harta se aseaza `preset`; nodurile noi se pun langa
     vecinii lor, iar cei fara vecini cu asezarea aleasa (`map/positions.ts`).
     Planul se face pe ce se deseneaza ca nod: cu casete, membrii si casetele
     restranse (`grp:<cheie>`); o caseta deschisa sta unde ii sunt membrii. */
  const plan = presetPlan(planGraph(options), options.positions)

  const cy = cytoscape({
    container: options.container,
    elements,
    style: [...mapStyle(), ...arrangeStyle()],
    /* Cu pozitii salvate: exact ele. Altfel nodurile pornesc toate din acelasi
       punct, iar unde stau o hotaraste `arrangeMap()`, imediat dedesubt - si
       numai el, ca forta sa nu ruleze de doua ori. */
    layout: plan ? presetLayout(plan) : { name: 'preset', fit: false },
    wheelSensitivity: 0.2,
    maxZoom: 3,
    /* Destul de jos cat sa incapa cateva sute de noduri pe ecran. La vechiul
       prag de 0.2, un parc intreg era taiat pe margini si nu exista fel de a
       da inapoi destul cat sa se vada. */
    minZoom: 0.05,
  })

  if (plan) {
    /* Pozitiile salvate NU se reaseaza: doar orfanii (noduri noi fara vecin
       asezat), cu asezarea aleasa, in cutia lor. */
    layoutOrphans(cy, plan, layoutFor(options.view, plan.orphans.length, options.layout ?? 'auto'))
  } else {
    arrangeMap(cy as unknown as ArrangeCy, { view: options.view, arrange: options.layout ?? 'auto', root: options.root })
  }

  /*
   * Incadrata dupa ce asezarea s-a terminat, nu in timp ce ruleaza: cytoscape
   * aseaza si picteaza in aceeasi bataie, deci un `fit` dat imediat masoara
   * pozitii care tocmai se schimba - de-aia graful ajungea inghesuit intr-un
   * colt.
   *
   * Si numai daca omul nu l-a mutat el: schimbarea unui filtru redeseneaza
   * harta de la zero, iar `fit` o strangea la loc - deci cine marise o bucata
   * ca s-o citeasca o pierdea la fiecare schimbare de vedere.
   */
  cy.ready(() => {
    if (options.viewport) {
      cy.zoom(options.viewport.zoom)
      cy.pan(options.viewport.pan)
      return
    }

    cy.fit(null, 25)

    /* Pe un graf mic, umplerea panzei umfla nodurile cat niste butoane. */
    if (cy.zoom() > 1.4) {
      cy.zoom({ level: 1.4, renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } })
      cy.center()
    }
  })

  cy.on('dragfree', 'node', () => options.onNodesMoved?.(readPositions(cy)))

  cy.on('tap', 'node', (ev) => {
    const id = String((ev.target.data as (k: string) => string)('id'))
    /* O caseta nu are fisa de impact: clicul o restrange sau o deschide. */
    const key = groupKeyFromId(id)
    if (key !== null) {
      options.onGroup?.(key)
      return
    }
    options.onNode(Number(id))
  })

  /*
   * Ce a facut omul cu harta, tinut minte. `zoom pan` bate si la `fit`, deci
   * n-ar fi fost de ajuns singur: steagul se ridica numai la o miscare pornita
   * de el - rotita, tras cu mausul, atins pe ecran.
   */
  cy.on('zoom pan', () => options.onViewportChange({ zoom: cy.zoom(), pan: cy.pan() }))
  cy.on('mousedown touchstart scrollzoom pinchzoomstart', () => options.onViewportMoved())
  cy.container().addEventListener('wheel', () => options.onViewportMoved(), { passive: true })

  return cy
}
